import { pbQuery } from "@sergio9929/pb-query";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { deleteCollectionItemModal } from "@/components/admin/DeleteModal";
import type { CollectionItem } from "@/hooks/use-delete-collection-items";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import { branchesQueries } from "@/modules/branches/queries";
import { doctorBranchRolesQueries } from "@/modules/doctorBranchRolesQueries";
import { doctorsQueries } from "@/modules/doctors/queries";
import { invitationsQueries } from "@/modules/invitations/queries";
import { Collections, type DoctorBranchRolesResponse } from "@/types/pocketbase";

const createDoctorBranchRoleItems = (roles: Array<DoctorBranchRolesResponse>): Array<CollectionItem> =>
	roles.map((dbr) => ({
		collection: Collections.DoctorBranchRoles,
		id: dbr.id,
	}));

type Props = {
	branchId?: string;
};

export const useRemoveDoctors = ({ branchId }: Props) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const removeDoctors = useCallback(
		async ({ ids, onSuccess }: { ids: Array<string>; onSuccess?: () => void }) => {
			if (!branchId) return;

			try {
				const doctorBranchRoleQuery = pbQuery<DoctorBranchRolesResponse>()
					.equal("branch", branchId)
					.and()
					.in("doctor", ids)
					.build((raw, params) => pb.filter(raw, params));

				const doctorBranchRoles = await queryClient.ensureQueryData(
					doctorBranchRolesQueries.fullList({ options: doctorBranchRoleQuery })
				);

				const items = createDoctorBranchRoleItems(doctorBranchRoles);

				deleteCollectionItemModal.open({
					items,
					onSuccess: () => {
						queryClient.invalidateQueries({ queryKey: doctorsQueries.all() });
						queryClient.invalidateQueries({ queryKey: invitationsQueries.all() });
						queryClient.invalidateQueries({ queryKey: doctorBranchRolesQueries.all() });
						queryClient.invalidateQueries({ queryKey: branchesQueries.all() });

						onSuccess?.();
					},
				});
			} catch (error) {
				const parsed = parsePocketBaseError(error, {});
				toast.error(
					t(($) => $.modules.doctors.removeFromBranch.status.error.title),
					{
						id: "remove-doctor",
						description: parsed.message || t(($) => $.modules.doctors.removeFromBranch.status.error.message),
					}
				);
			}
		},

		[branchId, queryClient, t]
	);

	return { removeDoctors };
};
